import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { forceRefresh } from './tokenStore';
import { useThumbnailToken } from './useThumbnailToken';

function buildSrc(token, { f, w, d }, retry) {
  const params = new URLSearchParams({ f, w: String(w) });
  if (d) params.set('d', d);
  params.set('exp', String(token.exp));
  params.set('scope', token.scope);
  params.set('sig', token.sig);
  if (retry) params.set('retry', '1'); // cache-bust so the browser refetches
  return `${token.baseUrl}?${params.toString()}`;
}

const SignedThumbnail = ({ f, w, d, alt = '', fallback = null, onLoadError, ...imgProps }) => {
  const token = useThumbnailToken();
  const retried = useRef(false);
  const [failed, setFailed] = useState(false);
  const [freshToken, setFreshToken] = useState(null);

  if (!token || failed) return <>{fallback}</>;

  // Check 2 (reactive): <img> onError can't see the HTTP status, so on ANY error
  // assume an expired token once — refresh (deduped) and retry with the new sig.
  // A second failure is a genuine error (404 etc.).
  const handleError = () => {
    if (retried.current) {
      setFailed(true);
      if (onLoadError) onLoadError();
      return;
    }
    retried.current = true;
    forceRefresh()
      .then(setFreshToken)
      .catch(() => {
        setFailed(true);
        if (onLoadError) onLoadError();
      });
  };

  const effectiveToken = freshToken || token;
  return (
    <img
      loading="lazy"
      {...imgProps}
      alt={alt}
      src={buildSrc(effectiveToken, { f, w, d }, retried.current)}
      onError={handleError}
    />
  );
};

SignedThumbnail.propTypes = {
  /** File name — the 'f' query param (raw, URLSearchParams handles encoding) */
  f: PropTypes.string.isRequired,
  /** Thumbnail size — the 'w' query param */
  w: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Optional damage id — the 'd' query param */
  d: PropTypes.string,
  alt: PropTypes.string,
  /** Rendered while the token is loading or after a genuine load failure */
  fallback: PropTypes.node,
  onLoadError: PropTypes.func
};

export default SignedThumbnail;
